/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from "../../model/locale";
import { LocationUrls } from "../../model/navigation";
import {
  AnalyticsLogger,
  DiagnosticLogger,
  Log,
  OperationalLogger
} from "../../model/logging";
import { TMyHubActionType, TMyHubBulkActionType } from "../../model/types";
import type { TAnalyticsFields, TCarouselItem } from "../../model/types";
import { DiagnosticSeverity } from "../../model/types";
import { handleTileSelectionPlayback } from "../utility/smartCarouselUtility";
import { interpolate } from "./myHubSavedMappers";
import {
  cancelSeriesRecordings,
  deleteAllSeriesRecordings,
  deleteMyHubRecording,
  extendMyHubRecording,
  permanentlyDeleteAllRecordings,
  permanentlyDeleteMyHubRecording,
  refreshMyHubAfterAction,
  saveMyHubRecording,
  stopMyHubRecording,
  undeleteMyHubRecording,
  unsaveMyHubRecording
} from "./myHubSlice";
import {
  MY_HUB_DELETED_BULK_LIST_NAME,
  MY_HUB_FOLDER_ACTIONS_LIST_NAME
} from "./myHub.types";
import { loadSeriesRecordingOptions } from "./seriesRecordingOptions";
import { showUnityDialog } from "../unityDialog";
import type { UnityAppDispatch } from "../UnityAppStore";

const TAG: string = "myHubActionHandlers";

/** DiagnosticLogger category shared by every My Hub lifecycle/debug event. */
const DIAGNOSTIC_CATEGORY: string = "my_hub_lifecycle";

/** Screen identity for uiError attribution — see {@link OperationalLogger.logUiError}. */
const MY_HUB_SCREEN_ID: string = "my_hub";
const MY_HUB_SCREEN_NAME: string = "MyHub";

/**
 * Everything a handler needs besides the tile it acts on.
 *
 * Only what a tile cannot carry: the tile says *what* to act on, this says how
 * to reach the store, the router and the surface the press came from.
 */
export interface TMyHubActionContext {
  /** Store dispatch, for the thunks the recordings actions will issue. */
  dispatch: UnityAppDispatch;
  /** Router navigation, for the actions that leave the current screen. */
  navigateTo: (
    url: string,
    replace?: boolean,
    state?: Record<string, unknown>
  ) => void;
  /**
   * Closes the surface the action was picked from — a popup, an overlay.
   *
   * Called by {@link performMyHubAction} itself, once, before the handler runs:
   * picking any action dismisses the surface it was picked from. Undefined for
   * a surface that is not a popup (a Saved/Deleted row, a manage-series row),
   * where there is nothing to dismiss.
   */
  dismissSurface?: () => void;
  /**
   * Caller's log tag — console (`Log.*`) output only.
   *
   * Deliberately not telemetry: it is a component name
   * ("MyHubSavedListContainer"), and every analytics/operational field that
   * names a screen takes {@link screenId}/{@link screenName} instead, so those
   * dimensions stay closed sets rather than a mix of screen ids and class
   * names.
   */
  tag: string;
  /** Canonical screen id for this surface — `MY_HUB_SCREEN_ID`. */
  screenId: string;
  /** Canonical screen name for this surface — `MY_HUB_SCREEN_NAME`. */
  screenName: string;
  /**
   * The caller's screen-scoped analytics logger — `myHubTabAnalyticsLogger(filter)`
   * for a tab surface, `manageSeriesAnalyticsLogger()` for the manage-series
   * surfaces. Every `contentSelected`/`itemSelected`/`viewedContentDetails`
   * call below goes through this rather than the bare `AnalyticsLogger`, so
   * `screen_id`/`screen_name` are never blank: the base class has no screen of
   * its own to report.
   */
  analyticsLogger: typeof AnalyticsLogger;
  /**
   * The spec's `list_name` for this item: the series/collection title for a
   * manage-series/scheduled-series folder row, "Saved"/"Deleted" for the
   * flat-list tabs, or the carousel row's title for the recording-highlight
   * popup (opened off a My Hub grid tile, so it carries the row it came
   * from).
   */
  listName: string;
  /**
   * The item's `list_position`: a flat-list/folder row index for the
   * Saved/Deleted/manage-series surfaces, or the carousel row's index for the
   * recording-highlight popup — mirrors {@link listName}. Undefined only
   * where no caller has wired a real value through yet.
   */
  listPosition?: number;
  /**
   * The item's `tile_position` within `listName` — the tile's index inside
   * its carousel row, for the recording-highlight popup only. The flat-list
   * surfaces (Saved/Deleted, manage-series) are one-dimensional row lists, so
   * they report `listPosition` alone and leave this undefined.
   */
  tilePosition?: number;
}

/**
 * Maps a mapped tile's fields onto the recording/OnePass event fields the
 * spec lists (station/channel identity, season/episode, airing time, genre) —
 * shared by every handler below that fires one of those events for a single
 * item, so the mapping is written once.
 *
 * Exported for the views: a folder tile never reaches these handlers (opening
 * one goes straight to the manage-series screen, not through
 * {@link performMyHubAction}), so the screen that opens it logs its own
 * `contentSelected` off this same mapping rather than duplicating it.
 */
export const buildItemAnalyticsFields = (
  item: TCarouselItem
): Partial<TAnalyticsFields> => {
  const year: number | undefined =
    item.movieYear !== undefined ? item.movieYear : item.premierYear;
  return {
    content_id: item.contentId,
    content_title: item.title,
    content_type: item.contentType,
    collection_id: item.unifiedCollectionId,
    station_id: item.stationId,
    channel_id: item.stationId,
    channel_call_sign: item.channelName,
    channel_num: item.channelNumber,
    season_number:
      item.seasonNumber !== undefined ? String(item.seasonNumber) : undefined,
    episode_number:
      item.episodeNumber !== undefined ? String(item.episodeNumber) : undefined,
    year: year !== undefined ? String(year) : undefined,
    genre: item.categoryLabel,
    program_start_time: item.startTime,
    content_duration:
      item.duration !== undefined ? String(item.duration) : undefined
  };
};

/** Action ids for the confirmation dialogs' buttons. */
const CONFIRM_ACTION_ID: string = "confirm";
const DISMISS_ACTION_ID: string = "dismiss";

/**
 * Thrown by an action whose confirmation the user declined.
 *
 * Not an error in any real sense — it is how a handler tells
 * {@link performMyHubAction} that nothing happened, so the refresh every
 * successful action runs is skipped along with the failure logging. Without
 * it, cancelling a Permanently Delete would still refetch the list and drag
 * the cursor onto the first row, which is precisely what the user declined.
 */
class MyHubActionCancelledError extends Error {
  public constructor(actionType: string) {
    super(`My Hub action ${actionType} was cancelled`);
    this.name = "MyHubActionCancelledError";
  }
}

/** What {@link confirmDestructiveAction} renders. */
interface TDestructiveConfirmation {
  title: string;
  message: string;
  /** Label for the destructive button — "Permanently Delete", "Delete All", ... */
  acceptLabel: string;
}

/**
 * Raises a destructive-action confirmation and resolves once the user has
 * answered it — `true` for the destructive button, `false` for Cancel or BACK.
 *
 * Awaited rather than fire-and-forget (which is what the folder-level bulk
 * actions below do) because an item-scoped action's caller refreshes the
 * surface as soon as the handler resolves: resolving at the point the dialog
 * *opens* would refresh behind the dialog and move the cursor while the user
 * is still deciding.
 *
 * Cancel is focused by default — the safer choice is the one a stray OK should
 * land on — and BACK answers the same way Cancel does, so the promise cannot
 * be left unsettled by the one input that closes the dialog without touching a
 * button.
 */
const confirmDestructiveAction = async (
  dispatch: UnityAppDispatch,
  confirmation: TDestructiveConfirmation
): Promise<boolean> =>
  new Promise<boolean>((resolve: (isConfirmed: boolean) => void): void => {
    dispatch(
      showUnityDialog({
        title: confirmation.title,
        message: confirmation.message,
        defaultFocusedActionId: DISMISS_ACTION_ID,
        onBackPress: (): void => {
          resolve(false);
        },
        actions: [
          {
            id: DISMISS_ACTION_ID,
            label: Locale.getString(LocaleKeys.ACTION_CANCEL),
            onPress: (): void => {
              resolve(false);
            }
          },
          {
            id: CONFIRM_ACTION_ID,
            label: confirmation.acceptLabel,
            onPress: (): void => {
              resolve(true);
            }
          }
        ]
      })
    );
  });

/**
 * One action's behaviour.
 *
 * Takes the mapped tile straight: every My Hub surface that offers buttons is
 * showing tiles, and a tile already carries the id, the offer type, the content
 * id and the actions the item affords — there is nothing left for a per-surface
 * wrapper shape to add.
 *
 * Async because playback is.
 */
type TMyHubActionHandler = (
  item: TCarouselItem,
  context: TMyHubActionContext
) => Promise<void>;

/**
 * The id the recordings service addresses this item by.
 *
 * A tile's `id` is its `myHubItemId`, which is not what the recordings
 * endpoints take — a recording carries its own `recordingId` beside it. Falls
 * back to `id` so a tile without one behaves as it did before.
 */
const resolveRecordingId = (item: TCarouselItem): string =>
  item.recordingId !== undefined && item.recordingId !== ""
    ? item.recordingId
    : item.id;

/**
 * `list_name`/`list_position`/`tile_position` for whichever list `item` sits
 * in, shared by every handler below that logs a
 * `contentSelected`/`itemSelected` press — see
 * {@link TMyHubActionContext.listName}.
 */
const buildListAnalyticsFields = (
  context: TMyHubActionContext
): Partial<TAnalyticsFields> => ({
  list_name: context.listName,
  list_position:
    context.listPosition !== undefined
      ? String(context.listPosition)
      : undefined,
  tile_position:
    context.tilePosition !== undefined
      ? String(context.tilePosition)
      : undefined
});

/**
 * Plays the item.
 *
 * Routed through {@link handleTileSelectionPlayback} — the same helper the home
 * rails, the See More grid and Search select through — rather than building a
 * playable here, so the offer-type fork, the NDVR request and the future-airing
 * guard are decided once. Navigation is skipped when playback did not start:
 * the player screen has nothing to show otherwise.
 */
const playAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const { navigateTo, tag, analyticsLogger } = context;

  // The helper keys the NDVR request off the tile's `id`, so it is handed one
  // whose id is the recording id. A scheduled item has no recordingId and plays
  // live off its stationId, where the fallback leaves the tile unchanged.
  const didStartPlayback: boolean = await handleTileSelectionPlayback(
    { ...item, id: resolveRecordingId(item) },
    tag
  );

  if (!didStartPlayback) {
    Log.w(TAG, `${tag} — playback did not start for id=${item.id}`);
    return;
  }

  analyticsLogger.logContentSelected({
    ...buildListAnalyticsFields(context),
    ...buildItemAnalyticsFields(item),
    content_id: item.id
  });
  navigateTo(LocationUrls.PLAYER);
};

/**
 * Opens the content detail screen.
 *
 * Always a `contentId` seed: every surface that offers this action shows a
 * single explicit recording or movie, never a series, so there is no
 * unifiedCollectionId case to fork on.
 */
const detailsAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const { navigateTo, tag, analyticsLogger } = context;

  if (item.contentId === undefined || item.contentId === "") {
    Log.w(TAG, `${tag} — details requested without a contentId`);
    return;
  }

  const itemFields: Partial<TAnalyticsFields> = buildItemAnalyticsFields(item);
  const listFields: Partial<TAnalyticsFields> =
    buildListAnalyticsFields(context);
  analyticsLogger.logContentSelected({
    ...listFields,
    ...itemFields
  });
  analyticsLogger.logViewedContentDetails({
    ...listFields,
    ...itemFields,
    source_screen_id: context.screenId
  });
  navigateTo(LocationUrls.CONTENT_DETAIL, undefined, {
    seedType: "contentId",
    seedId: item.contentId
  });
};

/**
 * Deletes the recording — recoverable, it moves to the Deleted tab.
 *
 * The interactor call lives in {@link deleteMyHubRecording}, not here — what
 * refreshes afterwards is {@link performMyHubAction}'s job, common to every
 * action. `.unwrap()` is what makes that conditional on success: the thunk
 * swallows its own interactor error into a `rejected` action (it still has
 * to, for its own toast), so without unwrapping, `await`ing the dispatch
 * would resolve normally either way and the refresh would fire on a failed
 * delete too.
 */
const deleteAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — delete recordingId=${recordingId}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "delete"
  });
  // Fires `deleteRecording` on success — see
  // RecordingsServiceInteractor.deleteRecordingsByRecordingIdsV2().
  await context
    .dispatch(
      deleteMyHubRecording({
        recordingId,
        analyticsFields: {
          screen: context.screenName,
          ...buildItemAnalyticsFields(item)
        }
      })
    )
    .unwrap();
};

/**
 * Saves the recording past its expiry, and unsaves it again.
 *
 * Saved state is its own resource on the recordings service —
 * `PUT`/`DELETE /v1/recordings/recordingId/{recordingId}/save` — addressed by
 * the recordingId alone. Deliberately not the schedule's `keepUntil` option:
 * that is retention policy on a scheduled recording, a different thing from
 * the saved state this tab is built around, and writing it would mean a
 * read-modify-write of the whole schedule.
 *
 * The call lives in {@link saveMyHubRecording}, not here, and `.unwrap()` is
 * what keeps the refresh {@link performMyHubAction} runs afterwards
 * conditional on it succeeding — an unsaved row leaves the Saved tab and the
 * saved-hours allowance in the summary line moves with it, but only once the
 * service has agreed. See {@link deleteAction} for why the unwrap is needed.
 */
const saveAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — save recordingId=${recordingId}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "save"
  });
  await context
    .dispatch(
      saveMyHubRecording({
        recordingId,
        analyticsFields: { screen: context.screenName }
      })
    )
    .unwrap();
};

/**
 * Drops the recording's saved status, letting it expire again — the
 * counterpart to {@link saveAction}, see it for the endpoint and the refresh.
 */
const unsaveAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — unsave recordingId=${recordingId}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "unsave"
  });
  await context
    .dispatch(
      unsaveMyHubRecording({
        recordingId,
        analyticsFields: { screen: context.screenName }
      })
    )
    .unwrap();
};

/**
 * Stops an in-progress recording, keeping what has been recorded so far.
 *
 * Cancels the schedule rather than deleting the item: what is already
 * recorded stays, which is what separates Stop from Delete.
 */
const stopAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — stop recordingId=${recordingId}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "stop"
  });
  // Fires `cancelRecording` on success — see
  // RecordingsServiceInteractor.deleteRecordingScheduleV2().
  await context
    .dispatch(
      stopMyHubRecording({
        recordingId,
        analyticsFields: {
          screen: context.screenName,
          ...buildItemAnalyticsFields(item)
        }
      })
    )
    .unwrap();
};

/**
 * Extends a recording's expiry window.
 *
 * TODO(UAW): No extend endpoint is wrapped, and the popup renders no button
 * for it either — see the unrenderable list in `recordingHighlightMappers`.
 */
const extendAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — extend requested for id=${item.id}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "extend"
  });
  await context.dispatch(extendMyHubRecording(recordingId)).unwrap();
};

/**
 * Restores a deleted recording. See {@link deleteAction} for why the service
 * call and the refetch live in the thunk rather than here.
 */
const undeleteAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — undelete recordingId=${recordingId}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "undelete"
  });
  // Fires `modifyRecording` on success — see
  // RecordingsServiceInteractor.recoverRecording().
  await context
    .dispatch(
      undeleteMyHubRecording({
        recordingId,
        analyticsFields: {
          screen: context.screenName,
          reason: "undelete",
          ...buildItemAnalyticsFields(item)
        }
      })
    )
    .unwrap();
};

/**
 * Removes a deleted recording for good.
 *
 * Destructive and irreversible — the one row action that cannot be undone by
 * pressing the button beside it — so it confirms before issuing anything, and
 * a declined confirmation cancels the action outright (see
 * {@link MyHubActionCancelledError}) rather than falling through to the
 * refresh. See {@link deleteAction} for why the service call and the toast
 * live in the thunk.
 */
const permanentlyDeleteAction = async (
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  const recordingId: string = resolveRecordingId(item);
  Log.i(TAG, `${context.tag} — permanently delete requested for id=${item.id}`);
  context.analyticsLogger.logItemSelected({
    ...buildListAnalyticsFields(context),
    tile_id: item.id,
    reason: "permanentlyDelete"
  });

  const isConfirmed: boolean = await confirmDestructiveAction(
    context.dispatch,
    {
      title: Locale.getString(
        LocaleKeys.MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_TITLE
      ),
      message: interpolate(
        Locale.getString(
          LocaleKeys.MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_MESSAGE
        ),
        item.title
      ),
      acceptLabel: Locale.getString(
        LocaleKeys.MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_ACCEPT
      )
    }
  );

  if (!isConfirmed) {
    Log.i(TAG, `${context.tag} — permanent delete declined for id=${item.id}`);
    throw new MyHubActionCancelledError(TMyHubActionType.PERMANENTLY_DELETE);
  }

  // Fires `deleteRecording` with `is_soft_delete: "false"` on success — see
  // RecordingsServiceInteractor.permanentlyDeleteRecording().
  await context
    .dispatch(
      permanentlyDeleteMyHubRecording({
        recordingId,
        analyticsFields: {
          screen: context.screenName,
          ...buildItemAnalyticsFields(item)
        }
      })
    )
    .unwrap();
};

/**
 * What each My Hub action does, for every action the service can offer.
 *
 * A total `Record` rather than a `Partial` one: a new action type added to
 * {@link TMyHubActionType} fails to compile here until it is given behaviour,
 * which is what keeps a service-driven button from reaching a surface with
 * nothing behind it.
 */
const MY_HUB_ACTION_HANDLERS: Readonly<
  Record<TMyHubActionType, TMyHubActionHandler>
> = {
  [TMyHubActionType.PLAY]: playAction,
  [TMyHubActionType.DETAILS]: detailsAction,
  [TMyHubActionType.DELETE]: deleteAction,
  [TMyHubActionType.SAVE]: saveAction,
  [TMyHubActionType.UNSAVE]: unsaveAction,
  [TMyHubActionType.STOP]: stopAction,
  [TMyHubActionType.EXTEND]: extendAction,
  [TMyHubActionType.UNDELETE]: undeleteAction,
  [TMyHubActionType.PERMANENTLY_DELETE]: permanentlyDeleteAction
};

/**
 * Carries out one My Hub action.
 *
 * The single entry point every My Hub surface calls. A view decides only *which*
 * action the user picked and *on what tile*; what that action does lives in the
 * table above, so the same press behaves identically wherever it is offered.
 *
 * Dismisses the surface first, before the handler runs: picking any action on
 * a popup (the recording highlight) closes it immediately, regardless of what
 * the action then does. A no-op on a surface that is not a popup, since
 * `dismissSurface` is undefined there.
 *
 * Every successful action also refreshes afterwards — whatever My Hub
 * surface is currently on screen (the carousel grid, the Saved/Deleted flat
 * list, or an open manage-series/scheduled-series folder), plus the
 * recording quota — and lands the cursor on its first item. "Successful"
 * covers Play and Details too (they resolve normally even when playback
 * doesn't start or a contentId is missing, so they still refresh — there is
 * just nothing left to navigate to) and, for the recordings actions below,
 * means the interactor call actually went through: each of those handlers
 * `.unwrap()`s its dispatch, so a real failure throws here and is caught
 * below instead of silently reaching the refresh. Play and Details both
 * navigate away from My Hub, so their refresh runs in the background while
 * the player/content-detail screen is up front: by the time a sticky return
 * brings the user back, the row is already showing whatever the service now
 * reports instead of a stale pre-navigation snapshot. Applies uniformly
 * wherever the action was picked from — a carousel tile via the
 * recording-highlight popup, a Saved/Deleted row, or a
 * manage-series/scheduled-series row.
 *
 * @param actionType - The action the user picked, as the service named it.
 * @param item - The mapped tile the action acts on.
 * @param context - Dispatch, navigation, the optional surface dismissal and the
 *   caller's log tag.
 */
export const performMyHubAction = async (
  actionType: TMyHubActionType,
  item: TCarouselItem,
  context: TMyHubActionContext
): Promise<void> => {
  if (context.dismissSurface !== undefined) {
    context.dismissSurface();
  }
  try {
    await MY_HUB_ACTION_HANDLERS[actionType](item, context);
    Log.d(
      TAG,
      `${context.tag} — action=${actionType} succeeded for id=${item.id}`
    );
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "performMyHubAction() succeeded",
      DiagnosticSeverity.Debug,
      { event: "action_succeeded", action_type: actionType, tag: context.tag }
    );
    context.dispatch(refreshMyHubAfterAction({ moveFocusToFirstItem: true }));
  } catch (error: unknown) {
    // A declined confirmation is not a failure and not a success: nothing was
    // issued, so there is nothing to refresh and nothing to report beyond the
    // handler's own log line.
    if (error instanceof MyHubActionCancelledError) {
      Log.d(
        TAG,
        `${context.tag} — action=${actionType} cancelled for id=${item.id}`
      );
      return;
    }
    // No `uiError` here: every action that can fail does so inside its own
    // thunk, which already logged one and raised the toast. `.unwrap()` only
    // re-throws that same failure, so emitting again would double-count every
    // My Hub error — and `logUiError` is documented as "fire once per distinct
    // occurrence". The diagnostic below is what ties the failure back to the
    // action the user actually pressed, which the thunk does not know.
    Log.e(
      TAG,
      `${context.tag} — action=${actionType} failed for id=${item.id}`,
      error
    );
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "performMyHubAction() failed",
      DiagnosticSeverity.Warning,
      {
        event: "action_failed",
        action_type: actionType,
        screen_id: context.screenId,
        error_message: OperationalLogger.describeErrorMessage(error)
      }
    );
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// TMyHubBulkActionType — collection/tab-scoped actions: the Manage Series
// screen's folder-action row (RECORDING_OPTIONS, START_SERIES_RECORDING,
// CANCEL_SERIES_RECORDINGS, DELETE_ALL_RECORDINGS) and the Deleted tab's flat-
// list bulk button (PERMANENTLY_DELETE_ALL). Same table + single-entry-point
// shape as the item-scoped actions above, just one level up: these act on a
// whole series or a whole tab, not a tile, so the context carries that
// identity instead.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything a bulk-action handler needs besides the surface it acts on.
 *
 * Mirrors {@link TMyHubActionContext} — same idea, one level up. Most fields
 * only make sense for the series-scoped actions and are blank ("" / 0) for
 * PERMANENTLY_DELETE_ALL, which is unscoped (see its handler below).
 */
export interface TMyHubBulkActionContext {
  /** Store dispatch, for the thunks these actions issue. */
  dispatch: UnityAppDispatch;
  /** The series/OnePass this action acts on. "" when not series-scoped. */
  unifiedCollectionId: string;
  /**
   * Series title, for the Recording Options overlay and confirm-dialog copy.
   * "" when not series-scoped.
   */
  seriesTitle: string;
  /**
   * Episode count, for the delete-confirmation copy ("All 12 recordings…").
   * Reused for PERMANENTLY_DELETE_ALL's count of deleted recordings, which
   * aren't episodes — name kept for the series actions it was written for.
   */
  episodeCount: number;
  /** The caller's screen-scoped analytics logger — see {@link TMyHubActionContext}. */
  analyticsLogger: typeof AnalyticsLogger;
  /** Canonical screen id/name for this surface — see {@link TMyHubActionContext}. */
  screenId: string;
  screenName: string;
  /**
   * Leaves the surface the action was picked from, for an action that empties
   * it outright.
   *
   * Supplied by the manage-series screen for DELETE_ALL_RECORDINGS: once every
   * episode is gone the folder has nothing left to show, so the screen exits
   * the same way BACK does — which refreshes the landing screen's carousel
   * grid (see `exitMyHubSeriesScreen`) and lands the user back on the tab the
   * folder was opened from.
   *
   * Undefined where the surface survives its own action — the Deleted tab's
   * PERMANENTLY_DELETE_ALL empties a tab, not a screen, so it stays put and
   * simply refreshes in place.
   */
  exitSurface?: () => void;
}

/**
 * One bulk action's behaviour.
 *
 * Async because a network call is — the two destructive actions actually
 * resolve as soon as the confirmation dialog is shown; the network call they
 * gate happens later, off the dialog's own confirm button.
 */
type TMyHubBulkActionHandler = (
  context: TMyHubBulkActionContext
) => Promise<void>;

/**
 * Opens the Series Recording Options overlay for this series.
 *
 * The overlay already exists and is mounted app-wide (see views/App.tsx), so
 * this only has to open it for the right collection.
 */
const recordingOptionsAction = async (
  context: TMyHubBulkActionContext
): Promise<void> => {
  const { dispatch, unifiedCollectionId, seriesTitle, analyticsLogger } =
    context;
  analyticsLogger.logItemSelected({
    list_name: MY_HUB_FOLDER_ACTIONS_LIST_NAME,
    tile_id: TMyHubBulkActionType.RECORDING_OPTIONS,
    reason: unifiedCollectionId
  });
  dispatch(
    loadSeriesRecordingOptions({
      ucid: unifiedCollectionId,
      title: seriesTitle,
      screen: MY_HUB_SCREEN_NAME,
      screenId: MY_HUB_SCREEN_ID
    })
  );
};

/**
 * Opens the same Series Recording Options overlay as {@link recordingOptionsAction},
 * in "create" mode: Save then POSTs a brand-new OnePass instead of PUTing an
 * existing one — the same `mode: "create"` Content Detail's own "Record
 * Series" already passes `loadSeriesRecordingOptions`.
 */
const startSeriesRecordingAction = async (
  context: TMyHubBulkActionContext
): Promise<void> => {
  const { dispatch, unifiedCollectionId, seriesTitle } = context;
  dispatch(
    loadSeriesRecordingOptions({
      ucid: unifiedCollectionId,
      title: seriesTitle,
      mode: "create",
      screen: MY_HUB_SCREEN_NAME,
      screenId: MY_HUB_SCREEN_ID
    })
  );
  context.analyticsLogger.logItemSelected({
    list_name: MY_HUB_FOLDER_ACTIONS_LIST_NAME,
    tile_id: TMyHubBulkActionType.START_SERIES_RECORDING,
    reason: context.unifiedCollectionId
  });
};

/**
 * Confirms, then cancels the series' OnePass — stops future episodes from
 * being scheduled.
 *
 * Acts on every recording in the series at once, so it never fires straight
 * off a keypress. The dismiss button is focused by default — the safer choice
 * is the one a stray OK should land on.
 */
const cancelSeriesRecordingsAction = async (
  context: TMyHubBulkActionContext
): Promise<void> => {
  const { dispatch, unifiedCollectionId, seriesTitle, analyticsLogger } =
    context;
  analyticsLogger.logItemSelected({
    list_name: MY_HUB_FOLDER_ACTIONS_LIST_NAME,
    tile_id: TMyHubBulkActionType.CANCEL_SERIES_RECORDINGS,
    reason: unifiedCollectionId
  });
  dispatch(
    showUnityDialog({
      title: Locale.getString(LocaleKeys.MY_HUB_SERIES_CANCEL_CONFIRM_TITLE),
      message: interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SERIES_CANCEL_CONFIRM_MESSAGE),
        seriesTitle
      ),
      defaultFocusedActionId: DISMISS_ACTION_ID,
      actions: [
        {
          id: DISMISS_ACTION_ID,
          label: Locale.getString(LocaleKeys.ACTION_CANCEL)
        },
        {
          id: CONFIRM_ACTION_ID,
          label: Locale.getString(
            LocaleKeys.MY_HUB_SERIES_CANCEL_CONFIRM_ACCEPT
          ),
          onPress: (): void => {
            void dispatch(
              cancelSeriesRecordings({ unifiedCollectionId, seriesTitle })
            );
          }
        }
      ]
    })
  );
};

/**
 * Confirms, then deletes every already-recorded episode of the series in one
 * bulk call. Same "dismiss focused by default" reasoning as cancel, above.
 */
const deleteAllRecordingsAction = async (
  context: TMyHubBulkActionContext
): Promise<void> => {
  const {
    dispatch,
    unifiedCollectionId,
    seriesTitle,
    episodeCount,
    analyticsLogger,
    exitSurface
  } = context;
  analyticsLogger.logItemSelected({
    list_name: MY_HUB_FOLDER_ACTIONS_LIST_NAME,
    tile_id: TMyHubBulkActionType.DELETE_ALL_RECORDINGS,
    reason: unifiedCollectionId
  });
  dispatch(
    showUnityDialog({
      title: Locale.getString(LocaleKeys.MY_HUB_SERIES_DELETE_CONFIRM_TITLE),
      message: interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SERIES_DELETE_CONFIRM_MESSAGE),
        String(episodeCount),
        seriesTitle
      ),
      defaultFocusedActionId: DISMISS_ACTION_ID,
      actions: [
        {
          id: DISMISS_ACTION_ID,
          label: Locale.getString(LocaleKeys.ACTION_CANCEL)
        },
        {
          id: CONFIRM_ACTION_ID,
          label: Locale.getString(
            LocaleKeys.MY_HUB_SERIES_DELETE_CONFIRM_ACCEPT
          ),
          onPress: (): void => {
            // What happens after the delete is decided here rather than in the
            // thunk, mirroring the item-scoped actions: the thunk knows the
            // call succeeded, only the caller knows whether the surface it
            // emptied is still worth refreshing.
            void (async (): Promise<void> => {
              const deletedCount: number = await dispatch(
                deleteAllSeriesRecordings({ unifiedCollectionId, seriesTitle })
              )
                .unwrap()
                .then(
                  (count: number): number => count,
                  // The thunk has already logged this failure and raised its
                  // own toast — swallowed here so it is not counted twice.
                  (): number => 0
                );
              // Nothing deleted, whether because the call failed or because
              // the folder held no recordings: the surface is unchanged, so
              // it is neither left nor refreshed.
              if (deletedCount === 0) {
                return;
              }
              if (exitSurface !== undefined) {
                exitSurface();
                return;
              }
              dispatch(refreshMyHubAfterAction());
            })();
          }
        }
      ]
    })
  );
};

/**
 * Confirms, then permanently deletes every recording on the Deleted tab.
 *
 * Unscoped, unlike the two actions above — it does not act on one series, so
 * `unifiedCollectionId`/`seriesTitle` go unused here; only `episodeCount`
 * (the Deleted tab's total, despite the name) and `dispatch` matter. Same
 * "dismiss focused by default" reasoning: irreversible, so it never fires
 * straight off a keypress.
 */
const permanentlyDeleteAllAction = async (
  context: TMyHubBulkActionContext
): Promise<void> => {
  const { dispatch, episodeCount, analyticsLogger } = context;
  analyticsLogger.logItemSelected({
    list_name: MY_HUB_DELETED_BULK_LIST_NAME,
    tile_id: TMyHubBulkActionType.PERMANENTLY_DELETE_ALL,
    reason: String(episodeCount)
  });
  dispatch(
    showUnityDialog({
      title: Locale.getString(
        LocaleKeys.MY_HUB_DELETED_PERMANENT_CONFIRM_TITLE
      ),
      message: interpolate(
        Locale.getString(LocaleKeys.MY_HUB_DELETED_PERMANENT_CONFIRM_MESSAGE),
        String(episodeCount)
      ),
      defaultFocusedActionId: DISMISS_ACTION_ID,
      actions: [
        {
          id: DISMISS_ACTION_ID,
          label: Locale.getString(LocaleKeys.ACTION_CANCEL)
        },
        {
          id: CONFIRM_ACTION_ID,
          label: Locale.getString(
            LocaleKeys.MY_HUB_DELETED_PERMANENT_CONFIRM_ACCEPT
          ),
          onPress: (): void => {
            void dispatch(permanentlyDeleteAllRecordings());
          }
        }
      ]
    })
  );
};

/**
 * What each folder-level action does, for every action the service can offer.
 *
 * A total `Record` rather than a `Partial` one: a new action type added to
 * {@link TMyHubBulkActionType} fails to compile here until it is given
 * behaviour — mirrors {@link MY_HUB_ACTION_HANDLERS} above.
 */
const MY_HUB_BULK_ACTION_HANDLERS: Readonly<
  Record<TMyHubBulkActionType, TMyHubBulkActionHandler>
> = {
  [TMyHubBulkActionType.RECORDING_OPTIONS]: recordingOptionsAction,
  [TMyHubBulkActionType.START_SERIES_RECORDING]: startSeriesRecordingAction,
  [TMyHubBulkActionType.CANCEL_SERIES_RECORDINGS]: cancelSeriesRecordingsAction,
  [TMyHubBulkActionType.DELETE_ALL_RECORDINGS]: deleteAllRecordingsAction,
  [TMyHubBulkActionType.PERMANENTLY_DELETE_ALL]: permanentlyDeleteAllAction
};

/**
 * Carries out one folder-level My Hub action.
 *
 * The single entry point the Manage Series screen calls. The screen decides
 * only *which* action the user picked and *for which series*; what that
 * action does lives in the table above, so the same button behaves
 * identically wherever it is offered. Mirrors {@link performMyHubAction}.
 *
 * @param actionType - The action the user picked, as the service named it.
 * @param context - Dispatch and the series identity the action acts on.
 */
export const performMyHubBulkAction = async (
  actionType: TMyHubBulkActionType,
  context: TMyHubBulkActionContext
): Promise<void> => {
  try {
    await MY_HUB_BULK_ACTION_HANDLERS[actionType](context);
    Log.d(
      TAG,
      `action=${actionType} succeeded for ucid=${context.unifiedCollectionId}`
    );
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "performMyHubBulkAction() succeeded",
      DiagnosticSeverity.Debug,
      {
        event: "bulk_action_succeeded",
        action_type: actionType,
        unifiedCollectionId: context.unifiedCollectionId
      }
    );
  } catch (error: unknown) {
    Log.e(
      TAG,
      `action=${actionType} failed for ucid=${context.unifiedCollectionId}`,
      error
    );
    OperationalLogger.logUiError({
      errorMessage: OperationalLogger.describeErrorMessage(error),
      screen: context.screenName,
      screenId: context.screenId
    });
  }
};
